import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { productKeys } from './keys';

import type { TypeGetSkusParams, TypeGetSkusResponse, TypeProduct } from '~/types/product.types';

/**
 * [상품관리] 상품 리스트 조회 API
 */
export const fetchSkus = async (params: TypeGetSkusParams): Promise<TypeGetSkusResponse> => {
  const { data } = await axiosInstance.get<TypeGetSkusResponse>(API_ENDPOINT.PRODUCT.GET_SKUS, { params });
  return data;
};

type UseGetSkusQueryOptions = {
  params: TypeGetSkusParams;
  enabled?: boolean;
  showGlobalDimOverlay?: boolean;
};

/**
 * [상품관리] 상품 리스트 조회 Query Hook
 *
 * @description 상품등록/수정, 운영상품조회, 교환상품추가에서 사용
 * - [운영상품조회] 상품 리스트 조회 (status = 'ACTIVE', isActive = true 필수)
 * - [교환 신청/조회] 교환 상품 추가 > 상품 리스트 조회 (status = 'ACTIVE', isActive = true 필수)
 */
export const useGetSkusQuery = ({ params, enabled = true, showGlobalDimOverlay = true }: UseGetSkusQueryOptions) => {
  return useQuery({
    queryKey: productKeys.skus(params),
    queryFn: () => fetchSkus(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalDimOverlay },
  });
};

const mockSku = (index: number, overrides: Partial<TypeProduct> = {}): TypeProduct => ({
  id: 1000 + index,
  skuCode: `M000001076${String(index).padStart(2, '0')}`,
  skuName: `(센터명)화주사상품${index}`,
  supplierList: [{ supplierCode: 'SU000013', supplierName: '테스트공급사' }],
  useMethod: '사용중',
  temperatureType: '냉장',
  mainCategory: '신선식품',
  middleCategory: '채소',
  thirdCategory: '잎채소',
  fourthCategory: '상추',
  shelfLifeType: '유통기한',
  availableDays: 30,
  sellAvailableDays: 20,
  sellDeadlineDays: 10,
  statusName: '등록 완료',
  createdDate: '2026-05-12',
  ...overrides,
});

const skusNormalResponse: TypeGetSkusResponse = {
  data: [
    mockSku(1),
    mockSku(2, {
      statusName: '등록 요청',
      supplierList: [
        { supplierCode: 'SU000013', supplierName: '테스트공급사' },
        { supplierCode: 'SU000014', supplierName: '두번째공급사' },
      ],
      temperatureType: '냉동',
      availableDays: 365,
    }),
    mockSku(3, { statusName: '등록 대기', supplierList: [], shelfLifeType: '제조일자', createdDate: '2026-05-13' }),
    mockSku(4, {
      skuName: '(센터명)이름이 아주 긴 화주사 상품명 — 말줄임 확인용 긴 상품명 텍스트',
      useMethod: '사용중단',
      temperatureType: '상온',
      fourthCategory: '',
      availableDays: 1200,
    }),
    mockSku(5, { createdDate: '2026-05-19' }),
  ],
  pagination: { listView: 50, listTotal: 5, cursor: 0 },
};

const skusEmptyResponse: TypeGetSkusResponse = {
  data: [],
  pagination: { listView: 50, listTotal: 0, cursor: 0 },
};

registerMockAPI<TypeGetSkusResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.PRODUCT.GET_SKUS,
  label: ['상품/상품등록수정/상품목록', '상품/운영상품조회/상품목록', '반품교환/교환신청/교환상품추가'],
  scenarios: [
    { name: '정상 응답', response: skusNormalResponse },
    { name: '검색 결과 0건', response: skusEmptyResponse },
  ],
});
